import React from 'react';

/**
 * BookedFollowupMeetingSection - Visar information om bokat uppföljningsmöte
 * Accordion som automatiskt döljs efter mötet har passerat
 * 
 * @param {Object} estimatData - Estimatdata med uppföljningsinformation
 * @param {boolean} expanded - Om sektionen är expanderad
 * @param {Function} onToggle - Callback för att toggla expansion
 */
const BookedFollowupMeetingSection = ({ estimatData, expanded, onToggle }) => {
  // Kontrollera om uppföljningsmöte är bokat
  const meetingAt = estimatData?.estimatFollowup?.meetingAt;
  const selectedSlot = estimatData?.estimatFollowup?.selectedSlot;
  const teamsJoinUrl = estimatData?.estimatFollowup?.teamsJoinUrl;
  
  if (!meetingAt && !selectedSlot) {
    return null;
  }

  // Om möte finns, kontrollera om det har passerat
  if (meetingAt) {
    const meetingDate = new Date(meetingAt);
    const now = new Date();
    const hasMeetingPassed = meetingDate.getTime() < now.getTime();
    
    // Dölj helt om mötet har passerat
    if (hasMeetingPassed) {
      return null;
    }
  }
  
  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm border border-blue-200">
      {/* Accordion header - klickbar */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer select-none bg-blue-50 hover:bg-blue-100 transition-colors"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <h2 className="text-lg font-semibold text-blue-800">
          📅 Uppföljningsmöte bokat
        </h2>
        <div className="flex items-center gap-3">
          {meetingAt && (
            <span className="text-sm text-blue-700 font-medium">
              {new Date(meetingAt).toLocaleDateString('sv-SE', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC'
              })} kl {new Date(meetingAt).toLocaleTimeString('sv-SE', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC'
              })}
            </span>
          )}
          {!meetingAt && selectedSlot && (
            <span className="text-sm text-blue-700 font-medium">
              {selectedSlot}
            </span>
          )}
          <svg 
            className={`w-5 h-5 text-blue-600 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {/* Accordion content - expanderbar */}
      {expanded && (
        <div className="p-4 bg-blue-50 border-t border-blue-200">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 mb-2">
              <strong>Fullständigt datum och tid:</strong>
            </p>
            {meetingAt ? (
              <div className="text-lg font-medium text-blue-900 mb-3">
                {new Date(meetingAt).toLocaleDateString('sv-SE', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: 'UTC'
                })} kl {new Date(meetingAt).toLocaleTimeString('sv-SE', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'UTC'
                })}
              </div>
            ) : selectedSlot && (
              <div className="text-lg font-medium text-blue-900 mb-3">
                {selectedSlot}
              </div>
            )}
            
            {teamsJoinUrl && (
              <div className="pt-3 border-t border-blue-200">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Länk till möte:</strong>
                </p>
                <a
                  href={teamsJoinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Gå med i Teams-möte
                </a>
              </div>
            )}
            
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs text-blue-600">
                💡 Denna sektion försvinner automatiskt efter att mötet har genomförts.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookedFollowupMeetingSection;

